import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { get, post } from '../lib/api';
import { Button, Checkbox, Field, Input, Select } from '../components/ui';
import { errText } from '../lib/toast';
import { cls } from '../lib/format';
import authArt from '../assets/auth-panel.webp';

/** Public website for this app host: app.X → X, dev-app.X → dev-www.X (local dev: same origin). */
export function siteUrl(path = '/') {
  const h = location.hostname;
  const site = h.startsWith('dev-app.') ? h.replace(/^dev-app\./, 'dev-www.') : h.startsWith('app.') ? h.slice(4) : null;
  return site ? `${location.protocol}//${site}${path}` : path;
}

const PERKS = [
  ['Live in minutes', '5 simulated machines stream real data from the moment you sign up.'],
  ['Any brand of machine', 'A Teltonika tracker reads runtime, battery and location from scrubbers, sweepers, generators and vans.'],
  ['QR on every machine', 'Operators report problems from their phone — no app, no account.'],
  ['Your data stays yours', 'Excel import/export, REST API and a full export whenever you want.'],
];

function Shell({ title, children, sub, aside = true }: { title: string; children: ReactNode; sub?: ReactNode; aside?: boolean }) {
  return (
    <div className="flex min-h-screen bg-bg">
      {aside && (
        <aside className="relative hidden w-[44%] max-w-xl flex-col justify-between overflow-hidden bg-ink-950 p-10 text-white lg:flex">
          <img src={authArt} alt="" aria-hidden className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-70 mix-blend-lighten" />
          <div aria-hidden className="pointer-events-none absolute inset-0 bg-gradient-to-b from-ink-950/30 via-ink-950/60 to-ink-950" />
          <a href={siteUrl('/')} className="relative flex items-center gap-2.5 text-lg font-bold tracking-[-0.02em]"><img src="/icon.svg" alt="" className="h-8 w-8 rounded-lg shadow-[0_0_0_1px_rgb(255_255_255/0.15),0_8px_24px_-8px_rgb(20_184_166/0.8)]" />AssetNetwork</a>
          <div className="relative">
            <h2 className="mb-7 text-[34px] font-bold leading-[1.08] tracking-[-0.03em]">Know what every machine did today <span className="bg-gradient-to-r from-brand-300 to-pulse-400 bg-clip-text text-transparent">— and fix it before it fails.</span></h2>
            <ul className="space-y-4">
              {PERKS.map(([t, d]) => (
                <li key={t} className="flex gap-3"><span className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-brand-500/20 text-[11px] text-brand-300 ring-1 ring-brand-400/40">✓</span><div><div className="font-semibold">{t}</div><div className="text-sm text-slate-400">{d}</div></div></li>
              ))}
            </ul>
          </div>
          <p className="relative text-xs text-slate-500">POPIA &amp; GDPR-ready · hosted on a dedicated server · cancel anytime</p>
        </aside>
      )}
      <main className="flex flex-1 items-center justify-center p-4 sm:p-8">
        <div className="anim-in w-full max-w-md">
          <a href={siteUrl('/')} className={cls('mb-6 flex items-center gap-2 text-lg font-bold tracking-[-0.02em] text-fg', aside && 'lg:hidden')}><img src="/icon.svg" alt="" className="h-8 w-8 rounded-lg" />AssetNetwork</a>
          <div className="rounded-2xl border border-line bg-surface p-6 shadow-an-md sm:p-8">
            <h1 className="mb-1 text-2xl font-semibold tracking-[-0.025em] text-fg">{title}</h1>
            {sub && <p className="mb-6 text-sm text-fg-muted">{sub}</p>}
            {!sub && <div className="mb-6" />}
            {children}
          </div>
          <p className="mt-6 text-center text-xs text-fg-subtle"><a className="hover:text-fg hover:underline" href={siteUrl('/privacy')}>Privacy</a> · <a className="hover:text-fg hover:underline" href={siteUrl('/terms')}>Terms</a> · <a className="hover:text-fg hover:underline" href={siteUrl('/contact')}>Help</a></p>
        </div>
      </main>
    </div>
  );
}

function Err({ e }: { e: string | null }) {
  return e ? <p role="alert" className="rounded-lg border border-red-500/25 bg-red-500/10 px-3 py-2 text-sm text-red-700">{e}</p> : null;
}

const useProviders = () => useQuery({ queryKey: ['providers'], queryFn: () => get<{ google: boolean; microsoft: boolean; signup: boolean }>('/v1/auth/providers'), staleTime: 300_000 });

const GoogleMark = () => (
  <svg viewBox="0 0 48 48" className="h-5 w-5" aria-hidden><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z"/></svg>
);
const MicrosoftMark = () => (
  <svg viewBox="0 0 23 23" className="h-4 w-4" aria-hidden><path fill="#f35325" d="M1 1h10v10H1z"/><path fill="#81bc06" d="M12 1h10v10H12z"/><path fill="#05a6f0" d="M1 12h10v10H1z"/><path fill="#ffba08" d="M12 12h10v10H12z"/></svg>
);

/** "Continue with Google/Microsoft" — rendered first, with an "or" divider before the email form. */
function Sso({ next, verb = 'Continue' }: { next: string; verb?: string }) {
  const p = useProviders();
  if (!p.data?.google && !p.data?.microsoft) return null;
  const btn = 'flex w-full items-center justify-center gap-3 rounded-md border border-slate-300 bg-white py-2.5 text-sm font-medium text-slate-800 shadow-sm transition hover:bg-slate-50';
  return (
    <div className="mb-5 space-y-2">
      {p.data.google && <a className={btn} href={`/v1/auth/oidc/google/start?returnTo=${encodeURIComponent(next)}`}><GoogleMark />{verb} with Google</a>}
      {p.data.microsoft && <a className={btn} href={`/v1/auth/oidc/microsoft/start?returnTo=${encodeURIComponent(next)}`}><MicrosoftMark />{verb} with Microsoft</a>}
      <div className="flex items-center gap-3 pt-3 text-xs uppercase tracking-wide text-slate-400"><span className="h-px flex-1 bg-slate-200" />or with email<span className="h-px flex-1 bg-slate-200" /></div>
    </div>
  );
}

function PasswordInput(props: { value: string; onChange: (v: string) => void; autoComplete: string; minLength?: number }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <Input type={show ? 'text' : 'password'} required autoComplete={props.autoComplete} minLength={props.minLength} value={props.value} onChange={(e) => props.onChange(e.target.value)} className="pr-16" />
      <button type="button" onClick={() => setShow(!show)} className="absolute inset-y-0 right-0 px-3 text-xs font-medium text-slate-500 hover:text-slate-800" aria-label={show ? 'Hide password' : 'Show password'}>{show ? 'Hide' : 'Show'}</button>
    </div>
  );
}

export function Login() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [totp, setTotp] = useState('');
  const [needTotp, setNeedTotp] = useState(false);
  const [err, setErr] = useState<string | null>(sp.get('error'));
  const [busy, setBusy] = useState(false);
  const next = sp.get('next') ?? '/';
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      await post('/v1/auth/login', { email, password, ...(needTotp ? { totp } : {}) });
      await qc.invalidateQueries({ queryKey: ['me'] });
      nav(next.startsWith('/') ? next : '/', { replace: true });
    } catch (x) {
      if ((x as { code?: string }).code === 'totp_required') setNeedTotp(true);
      else setErr(errText(x));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Shell title="Welcome back" sub="Log in to your AssetNetwork account.">
      <Sso next={next} />
      <form onSubmit={submit} className="space-y-3">
        <Err e={err} />
        <Field label="Email"><Input type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} /></Field>
        <Field label="Password"><PasswordInput autoComplete="current-password" value={password} onChange={setPassword} /></Field>
        {needTotp && <Field label="Two-factor code" hint="6-digit code from your authenticator, or a recovery code"><Input inputMode="numeric" autoFocus value={totp} onChange={(e) => setTotp(e.target.value)} /></Field>}
        <Button variant="primary" className="w-full" busy={busy} type="submit">Log in</Button>
        <div className="flex justify-between text-sm"><Link className="text-brand-700 hover:underline" to="/forgot">Forgot password?</Link><Link className="font-medium text-brand-700 hover:underline" to="/signup">Start free trial</Link></div>
      </form>
    </Shell>
  );
}

export function Forgot() {
  const [email, setEmail] = useState('');
  const [done, setDone] = useState(false);
  return (
    <Shell title="Reset password">
      {done ? <p className="text-sm">If an account exists for {email}, a reset link is on its way. It is valid for 24 hours.</p> : (
        <form className="space-y-3" onSubmit={async (e) => { e.preventDefault(); await post('/v1/auth/forgot', { email }).catch(() => {}); setDone(true); }}>
          <Field label="Email"><Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} /></Field>
          <Button variant="primary" className="w-full" type="submit">Send reset link</Button>
        </form>
      )}
      <Link to="/login" className="mt-4 block text-sm text-brand-700">Back to login</Link>
    </Shell>
  );
}

function NewPassword({ title, endpoint, after, sub, cta = 'Continue' }: { title: string; endpoint: string; after: string; sub?: ReactNode; cta?: string }) {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [err, setErr] = useState<string | null>(null);
  return (
    <Shell title={title} sub={sub ?? 'At least 10 characters. Avoid passwords used elsewhere.'}>
      <form className="space-y-3" onSubmit={async (e) => { e.preventDefault(); if (pw !== pw2) return setErr('Passwords do not match'); try { await post(endpoint, { token: sp.get('token'), password: pw }); nav(after); } catch (x) { setErr(errText(x)); } }}>
        <Err e={err} />
        <Field label="New password"><Input type="password" autoComplete="new-password" minLength={10} required value={pw} onChange={(e) => setPw(e.target.value)} /></Field>
        <Field label="Repeat password"><Input type="password" autoComplete="new-password" required value={pw2} onChange={(e) => setPw2(e.target.value)} /></Field>
        <Button variant="primary" className="w-full" type="submit">{cta}</Button>
      </form>
    </Shell>
  );
}
export const Reset = () => <NewPassword title="Choose a new password" endpoint="/v1/auth/reset" after="/login" />;
export function Accept() {
  const [sp] = useSearchParams();
  const token = sp.get('token') ?? '';
  const inv = useQuery({ queryKey: ['invite', token], queryFn: () => get<{ organization: string; email: string | null; firstName: string | null; role: string; invitedBy: string | null }>(`/v1/auth/invite?token=${encodeURIComponent(token)}`), retry: false, enabled: !!token });
  if (inv.isError) return <Shell title="This invitation has expired" sub={errText(inv.error)}><Link className="text-sm font-medium text-brand-700 hover:underline" to="/login">Go to log in</Link></Shell>;
  const d = inv.data;
  return (
    <NewPassword
      title={d ? `Join ${d.organization}` : 'Accept invitation'}
      sub={d ? <>{d.invitedBy ? <><strong>{d.invitedBy}</strong> invited you</> : 'You were invited'} to join <strong>{d.organization}</strong> as {d.role}. Choose a password for <strong>{d.email}</strong> — at least 10 characters.</> : undefined}
      endpoint="/v1/auth/accept-invite"
      after="/"
      cta="Join and continue"
    />
  );
}

export function Signup() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const [step, setStep] = useState<'form' | 'code'>('form');
  const [f, setF] = useState({ email: '', password: '', firstName: '', lastName: '', acceptTerms: false, marketingOptIn: false });
  const [code, setCode] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [hint, setHint] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const providers = useProviders();
  const utm = Object.fromEntries([...sp.entries()].filter(([k]) => k.startsWith('utm_') || k === 'ref' || k === 'plan' || k === 'devices'));
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      const r = await post<{ teamHint: { orgName: string } | null }>('/v1/signup', { ...f, utm });
      if (r.teamHint) setHint(`Someone from your company already uses ${r.teamHint.orgName}. You can ask them for an invite instead — or continue to create your own trial.`);
      setStep('code');
    } catch (x) {
      setErr(errText(x));
    } finally {
      setBusy(false);
    }
  };
  if (step === 'code')
    return (
      <Shell title="Check your email" sub={`We sent a 6-digit code to ${f.email}. It expires in 15 minutes.`}>
        {hint && <p className="mb-3 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900">{hint}</p>}
        <form className="space-y-3" onSubmit={async (e) => { e.preventDefault(); setBusy(true); try { const r = await post<{ hasOrg: boolean }>('/v1/signup/verify', { email: f.email, code }); nav(r.hasOrg ? '/' : '/signup/org'); } catch (x) { setErr(errText(x)); } finally { setBusy(false); } }}>
          <Err e={err} />
          <Field label="Verification code"><Input inputMode="numeric" autoComplete="one-time-code" maxLength={6} autoFocus value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} /></Field>
          <Button variant="primary" className="w-full" busy={busy} type="submit">Verify</Button>
          <p className="text-sm text-slate-500">No email after a minute? Check spam, or <button type="button" className="font-medium text-brand-700 hover:underline" onClick={() => post('/v1/signup/resend', { email: f.email }).then(() => setHint('A new code is on its way.')).catch((x) => setErr(errText(x)))}>send a new code</button> · <button type="button" className="font-medium text-brand-700 hover:underline" onClick={() => setStep('form')}>change email</button></p>
        </form>
      </Shell>
    );
  if (providers.data && !providers.data.signup)
    return (
      <Shell title="Free trials are opening soon" sub="We're onboarding our first pilot customers by hand so every fleet gets set up properly.">
        <div className="space-y-3">
          <a href={siteUrl('/waitlist')} className="flex w-full items-center justify-center rounded-md bg-brand-700 px-4 py-2.5 text-sm font-medium text-white hover:bg-brand-800">Join the waitlist</a>
          <a href={siteUrl('/book-demo')} className="flex w-full items-center justify-center rounded-md border border-slate-300 px-4 py-2.5 text-sm font-medium text-slate-800 hover:bg-slate-50">Book a 30-minute demo</a>
        </div>
        <p className="mt-5 text-sm text-slate-500">Invited by your company? <Link className="font-medium text-brand-700 hover:underline" to="/login">Log in</Link> — or open the link in your invitation email.</p>
      </Shell>
    );
  return (
    <Shell title="Start your free trial" sub="14 days free · no credit card · live demo machines included">
      <Sso next="/signup/org" verb="Sign up" />
      <form className="space-y-3" onSubmit={submit}>
        <Err e={err} />
        <div className="grid grid-cols-2 gap-2">
          <Field label="First name" required><Input required autoComplete="given-name" value={f.firstName} onChange={(e) => setF({ ...f, firstName: e.target.value })} /></Field>
          <Field label="Last name"><Input autoComplete="family-name" value={f.lastName} onChange={(e) => setF({ ...f, lastName: e.target.value })} /></Field>
        </div>
        <Field label="Work email" required><Input type="email" autoComplete="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></Field>
        <Field label="Password" required hint="At least 10 characters"><PasswordInput autoComplete="new-password" minLength={10} value={f.password} onChange={(v) => setF({ ...f, password: v })} /></Field>
        <Checkbox checked={f.acceptTerms} onChange={(v) => setF({ ...f, acceptTerms: v })} label={<>I accept the <a className="underline" href={siteUrl('/terms')} target="_blank" rel="noreferrer">terms</a> and <a className="underline" href={siteUrl('/privacy')} target="_blank" rel="noreferrer">privacy policy</a></>} />
        <Checkbox checked={f.marketingOptIn} onChange={(v) => setF({ ...f, marketingOptIn: v })} label="Send me product news (optional)" />
        <Button variant="primary" className="w-full py-2.5" busy={busy} type="submit" disabled={!f.acceptTerms}>Create account</Button>
        {!f.acceptTerms && <p className="text-center text-xs text-slate-400">Tick the terms box to continue.</p>}
      </form>
      <p className="mt-5 text-sm text-slate-600">Already have an account? <Link className="font-medium text-brand-700 hover:underline" to="/login">Log in</Link></p>
    </Shell>
  );
}

const COUNTRIES = [['ZA', 'South Africa'], ['NA', 'Namibia'], ['BW', 'Botswana'], ['GB', 'United Kingdom'], ['DE', 'Germany'], ['NL', 'Netherlands'], ['FR', 'France'], ['US', 'United States']];

export function SignupOrg() {
  const qc = useQueryClient();
  const [f, setF] = useState({ company: '', country: 'ZA', industry: 'cleaning', fleetSizeBand: '11-50', useCases: [] as string[], demo: true });
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState(1);
  const toggle = (u: string) => setF({ ...f, useCases: f.useCases.includes(u) ? f.useCases.filter((x) => x !== u) : [...f.useCases, u] });
  return (
    <Shell title={['Your company', 'What do you want to do?', 'How do you want to start?'][step - 1]!} sub={`Step ${step} of 3`}>
      <Err e={err} />
      {step === 1 && (
        <div className="space-y-3">
          <Field label="Company name" required><Input autoFocus value={f.company} onChange={(e) => setF({ ...f, company: e.target.value })} /></Field>
          <Field label="Country" hint="Sets currency, timezone and locale defaults"><Select value={f.country} onChange={(e) => setF({ ...f, country: e.target.value })}>{COUNTRIES.map(([c, n]) => <option key={c} value={c}>{n}</option>)}</Select></Field>
          <Field label="Industry"><Select value={f.industry} onChange={(e) => setF({ ...f, industry: e.target.value })}><option value="cleaning">Cleaning / FM</option><option value="construction">Construction</option><option value="rental">Rental</option><option value="logistics">Logistics</option><option value="other">Other</option></Select></Field>
          <Field label="Fleet size"><Select value={f.fleetSizeBand} onChange={(e) => setF({ ...f, fleetSizeBand: e.target.value })}>{['1-10', '11-50', '51-200', '201-1000', '1000+'].map((x) => <option key={x}>{x}</option>)}</Select></Field>
          <Button variant="primary" className="w-full" disabled={f.company.length < 2} onClick={() => setStep(2)}>Next</Button>
        </div>
      )}
      {step === 2 && (
        <div className="space-y-2">
          {[['track', 'Track machines (runtime, location, battery)'], ['maintenance', 'Maintenance & checks'], ['qr', 'QR problem reporting'], ['vehicles', 'Vehicle fleet']].map(([k, l]) => <Checkbox key={k} label={l} checked={f.useCases.includes(k!)} onChange={() => toggle(k!)} />)}
          <div className="flex gap-2 pt-2"><Button onClick={() => setStep(1)}>Back</Button><Button variant="primary" className="flex-1" onClick={() => setStep(3)}>Next</Button></div>
        </div>
      )}
      {step === 3 && (
        <div className="space-y-3">
          <label className="block cursor-pointer rounded-lg border-2 p-3 has-[:checked]:border-brand-600"><input type="radio" className="mr-2" checked={f.demo} onChange={() => setF({ ...f, demo: true })} /><strong>Start with demo data</strong> (recommended)<div className="ml-5 text-sm text-slate-500">5 simulated machines stream live data so you can try everything right away. Remove it with one click.</div></label>
          <label className="block cursor-pointer rounded-lg border-2 p-3 has-[:checked]:border-brand-600"><input type="radio" className="mr-2" checked={!f.demo} onChange={() => setF({ ...f, demo: false })} /><strong>Empty account</strong><div className="ml-5 text-sm text-slate-500">Import your assets from Excel or add them by hand.</div></label>
          <div className="flex gap-2"><Button onClick={() => setStep(2)}>Back</Button><Button variant="primary" className="flex-1" busy={busy} onClick={async () => { setBusy(true); try { await post('/v1/signup/org', f); await qc.invalidateQueries(); location.href = '/'; } catch (x) { setErr(errText(x)); setBusy(false); } }}>Create my account</Button></div>
        </div>
      )}
    </Shell>
  );
}

/** HQ impersonation adoption: token arrives in the URL fragment. */
export function Impersonate() {
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    const token = location.hash.slice(1);
    history.replaceState(null, '', '/impersonate');
    post('/v1/auth/adopt', { token }).then(() => (location.href = '/')).catch((x) => setErr(errText(x)));
  }, []);
  return <Shell title="Opening support session…"><Err e={err} /></Shell>;
}
